import { getOrLoadBuffer } from './caches';
import type { BufferStem } from './types';

let lastAC: AbortController | null = null;

function getDecodeConcurrency(): number {
  // Default small to reduce peak memory; clamp to [1, 8]
  let n = 2;
  try {
    const env = (process as any)?.env?.REACT_APP_MEDIA_DECODE_CONCURRENCY;
    if (env != null && !Number.isNaN(Number(env))) n = Math.floor(Number(env));
  } catch {}
  try {
    const v = localStorage.getItem('MEDIA_DECODE_CONCURRENCY');
    if (v != null && !Number.isNaN(Number(v))) n = Math.floor(Number(v));
  } catch {}
  if (!isFinite(n) || n <= 0) n = 2;
  return Math.max(1, Math.min(8, n));
}

async function runWithConcurrency<T>(tasks: Array<() => Promise<T>>, concurrency: number): Promise<T[]> {
  const results: T[] = new Array(tasks.length) as any;
  let next = 0;
  let active = 0;
  return await new Promise<T[]>((resolve, reject) => {
    const maybeStart = () => {
      while (active < concurrency && next < tasks.length) {
        const idx = next++;
        active++;
        const p = tasks[idx]();
        p.then((val) => {
          results[idx] = val;
          active--;
          if (next >= tasks.length && active === 0) resolve(results);
          else maybeStart();
        }).catch((e) => {
          // On error, reject early and let caller decide (consistent with Promise.all semantics)
          reject(e);
        });
      }
    };
    if (tasks.length === 0) return resolve([]);
    maybeStart();
  });
}

function shouldNoStore(): boolean {
  try {
    const v = (process as any)?.env?.REACT_APP_MEDIA_FETCH_NOSTORE;
    if (v != null) return v === '1' || String(v).toLowerCase() === 'true';
  } catch {}
  try {
    const w = (window as any);
    if (w.__MEDIA_FETCH_NOSTORE === true) return true;
    const v2 = localStorage.getItem('MEDIA_FETCH_NOSTORE');
    if (v2 != null) return v2 === '1' || String(v2).toLowerCase() === 'true';
  } catch {}
  return false;
}

async function fetchWithRetry(url: string, init: RequestInit, shouldRetry: (status: number) => boolean, retries = 3, baseDelayMs = 300): Promise<Response> {
  let attempt = 0;
  let lastErr: any = null;
  while (attempt <= retries) {
    try {
      const res = await fetch(url, init);
      if (res.ok) return res;
      if (!shouldRetry(res.status) || attempt === retries) return res; // return even if not ok; caller will handle
    } catch (e) {
      lastErr = e;
      if (attempt === retries) throw e;
    }
    // Exponential backoff with jitter
    const delay = baseDelayMs * Math.pow(2, attempt) + Math.floor(Math.random() * 100);
    await new Promise(r => setTimeout(r, delay));
    attempt++;
  }
  if (lastErr) throw lastErr;
  // fallback: try one last fetch
  return await fetch(url, init);
}

async function fetchAndDecode(ctx: AudioContext, url: string, signal?: AbortSignal): Promise<AudioBuffer> {
  const noStore = shouldNoStore();
  const init: RequestInit = {
    mode: 'cors',
    cache: noStore ? 'no-store' : 'force-cache',
    headers: noStore ? { 'Cache-Control': 'no-store' } as any : undefined,
    signal,
  };
  const res = await fetchWithRetry(
    url,
    init,
    (status) => status === 502 || status === 503 || status === 504,
    3,
    250
  );
  if (!res.ok) {
    const ct = res.headers.get('content-type') || '';
    const snippet = ct.includes('text') || ct.includes('json') ? (await res.text().catch(() => ''))?.slice(0, 200) : '';
    const err = new Error(`HTTP ${res.status} fetching media ${url}${snippet ? ` :: ${snippet}` : ''}`);
    // Surface status code for upstream handling/logging
    // @ts-ignore
    err.status = res.status;
    throw err;
  }
  const ct = res.headers.get('content-type') || '';
  if (ct && !/audio\//i.test(ct)) {
    // Protect decodeAudioData from HTML error pages masquerading as audio
    const snippet = (await res.text().catch(() => ''))?.slice(0, 200);
    throw new Error(`Unexpected content-type '${ct}' for ${url}${snippet ? ` :: ${snippet}` : ''}`);
  }
  let arr: ArrayBuffer | null = await res.arrayBuffer();
  // Decode, then drop the raw ArrayBuffer reference so it can be GC'd
  const buf = await ctx.decodeAudioData(arr);
  arr = null;
  return buf;
}

export async function loadBuffers(
  ctx: AudioContext,
  stems: BufferStem[],
  onProgress?: (loaded: number, total: number) => void,
) {
  // Abort any in-flight fetch/decodes from a previous swap
  try { lastAC?.abort(); } catch {}
  const ac = new AbortController();
  lastAC = ac;

  const total = stems.length;
  let loaded = 0;

  // Prepare tasks, then run with a bounded concurrency to reduce peak memory.
  const tasks = stems.map((s) => async () => {
    const key = s.url;
    const buf = await getOrLoadBuffer(ctx, key, () => fetchAndDecode(ctx, key, ac.signal));
    loaded += 1; try { onProgress?.(loaded, total); } catch {}
    return [s.role, buf] as const;
  });
  const entries = await runWithConcurrency(tasks, getDecodeConcurrency());

  // Clear controller after successful completion
  if (lastAC === ac) lastAC = null;
  return Object.fromEntries(entries) as Record<string, AudioBuffer>;
}

export async function prefetchBuffers(
  ctx: AudioContext,
  stems: { url: string }[],
): Promise<void> {
  const promises = stems.map(({ url }) => getOrLoadBuffer(ctx, url, () => fetchAndDecode(ctx, url)));
  try { await Promise.allSettled(promises); } catch {}
}
